import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react"
import i18nData from "@rogue_4/locales/zh-CN.json"
import anime from "animejs"
import classNames from "classnames"

import { i18n } from "@/utils/i18n"
import ANIMATION from "../../animation"
import Button from "../../components/Button"
import GAME_EMITTER from "../../components/Emitter"
import { useGameShareModal } from "../../components/Modal/store"
import GAME_RESULT_DICT from "../../data/result"
import { useDivRef } from "../../hooks/useDivRef"
import { useGameStore } from "../../store"
import { IAnimateRef } from "../../type"
import { SFX } from "../../utils/sound"
import { useNextScene } from "../store"
import { TypeToast } from "../trail"

import styles from "./index.module.less"

const ResultScene = React.forwardRef<IAnimateRef>((_, ref) => {
    const gameResult = useGameStore((state) => state.result)
    const shared = useGameStore((state) => state.shared)

    const result = useMemo(
        () => (gameResult ? GAME_RESULT_DICT[gameResult.key] : undefined),
        [gameResult]
    )
    const callShareModal = useGameShareModal()
    const handleShare = useCallback(
        (e: React.MouseEvent) => {
            SFX.play("commonClick")
            e.stopPropagation()
            callShareModal()
        },
        [callShareModal]
    )
    const nextScene = useNextScene()
    const indexRef = useRef(0)
    const [toastText, setToastText] = useState("")
    const [buttonActive, setButtonActive] = useState(false)
    const lockNextRef = useRef(false)
    const onTypeEnd = useCallback(() => {
        lockNextRef.current = false
    }, [])
    const handleNext = useCallback(() => {
        if (lockNextRef.current) return
        if (!result) return
        indexRef.current = indexRef.current + 1
        if (indexRef.current >= result.toast.length) {
            setToastText("")
            setButtonActive(true)
            lockNextRef.current = true
        } else {
            setToastText(result.toast[indexRef.current])
            lockNextRef.current = true
        }
    }, [result])
    useEffect(() => {
        GAME_EMITTER.on("NEXT", handleNext)
        return () => GAME_EMITTER.off("NEXT", handleNext)
    }, [handleNext])

    const totalRef = useDivRef()

    useImperativeHandle(
        ref,
        () => ({
            fadeIn: async () => {
                const inst = anime.timeline()
                inst.add(
                    ANIMATION.in(
                        totalRef,
                        {},
                        { scale: [0.8, 1], duration: 800 }
                    ),
                    1200
                )
                inst.finished.then(() => {
                    setToastText(result?.toast[0] ?? "")
                })
                return inst.finished
            },
            fadeOut: async () => {
                const inst = anime.timeline()
                inst.add(ANIMATION.out(totalRef, {}, { duration: 500 }), 0)
                return inst.finished
            },
        }),
        [result, totalRef]
    )
    return (
        <div className={styles.resultScene} ref={totalRef}>
            <div
                className={styles.img}
                style={{
                    backgroundImage: result
                        ? `url("${result.image}")`
                        : undefined,
                }}
            ></div>
            <div className={styles.frame}>
                <div className={styles.title}>
                    <div className={styles.titleCn}>{result?.nameCn}</div>
                    <div className={styles.titleEn}>{result?.nameEn}</div>
                </div>
            </div>
            <div
                className={classNames(
                    styles.nextButton,
                    buttonActive && styles.active
                )}
            >
                {shared && (
                    <Button
                        active={false}
                        onClick={() => {
                            SFX.play("commonClick")
                            nextScene()
                        }}
                    >
                        {i18n(i18nData, "rogue_4.game.result.nextStep")}
                    </Button>
                )}
                <Button active={false} onClick={handleShare}>
                    {i18n(i18nData, "rogue_4.game.result.share")}
                </Button>
            </div>

            <TypeToast
                className={styles.toast}
                text={toastText}
                onTypeEnd={onTypeEnd}
            />
        </div>
    )
})
ResultScene.displayName = "ResultScene"

export default ResultScene
